import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { company } from '../data/content';
import { buildEstimateMessage, smsHrefFor, mailHrefFor } from '../utils/estimate';
import { CheckIcon, AlertIcon, PhoneIcon, MailIcon, MessageIcon, CameraIcon, PlusIcon } from './icons';
const MAX_PHOTOS = 4;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
interface PoolPhoto {
  file: File;
  url: string;
  id: string;
}
const ease = [0.23, 1, 0.32, 1] as const;
type ContactMethod = 'Call' | 'Text' | 'Email';
type Status = 'idle' | 'submitting' | 'success' | 'error';
interface FormValues {
  name: string;
  phone: string;
  email: string;
  zip: string;
  service: string;
  timeline: string;
  contactMethod: ContactMethod;
  details: string;
}
type FieldErrors = Partial<Record<keyof FormValues, string>>;
const serviceOptions = ['Pool plaster / replaster', 'Complete remodel', 'Tile work', 'Coping', 'Spray deck repair / resurfacing', 'Not sure — need an assessment'];
const timelineOptions = ['As soon as possible', 'Within 1–3 months', 'This season', 'Just getting pricing'];
const contactMethods: ContactMethod[] = ['Call', 'Text', 'Email'];
const initialValues: FormValues = {
  name: '',
  phone: '',
  email: '',
  zip: '',
  service: serviceOptions[0],
  timeline: timelineOptions[0],
  contactMethod: 'Call',
  details: ''
};
function validate(values: FormValues): FieldErrors {
  const errors: FieldErrors = {};
  if (!values.name.trim()) errors.name = 'Please tell us your name.';
  const digits = values.phone.replace(/\D/g, '');
  if (!digits) errors.phone = 'We need a number to reach you.';else if (digits.length < 10) errors.phone = 'Enter a 10-digit phone number.';
  if (values.contactMethod === 'Email' || values.email.trim()) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      errors.email = 'Enter a valid email address.';
    }
  }
  if (!values.zip.trim()) errors.zip = 'City or ZIP helps us schedule the visit.';
  return errors;
}
const inputClass = 'w-full rounded-md border bg-white px-3.5 py-3 text-[15px] text-navy-800 placeholder:text-navy-300 transition-colors duration-150 ease-out focus:outline-none focus:ring-2 focus:ring-sky-400';
export function EstimateForm() {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<Status>('idle');
  const [photos, setPhotos] = useState<PoolPhoto[]>([]);
  const [photoNotice, setPhotoNotice] = useState<string | null>(null);
  const [sharedWithPhotos, setSharedWithPhotos] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => () => {
    photos.forEach(photo => URL.revokeObjectURL(photo.url));
  }, [photos]);
  const update = <K extends keyof FormValues,>(key: K, value: FormValues[K]) => {
    setValues(prev => ({
      ...prev,
      [key]: value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
  };
  const addPhotos = (fileList: FileList | null) => {
    if (!fileList) return;
    const incoming = Array.from(fileList);
    const valid: PoolPhoto[] = [];
    let notice: string | null = null;
    incoming.forEach(file => {
      if (!file.type.startsWith('image/')) {
        notice = 'Only image files can be attached.';
        return;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        notice = `${file.name} is larger than 10 MB and was skipped.`;
        return;
      }
      valid.push({
        file,
        url: URL.createObjectURL(file),
        id: `${file.name}-${file.lastModified}`
      });
    });
    setPhotos(prev => {
      const merged = [...prev];
      valid.forEach(photo => {
        if (merged.length >= MAX_PHOTOS) {
          notice = `You can attach up to ${MAX_PHOTOS} photos.`;
          URL.revokeObjectURL(photo.url);
          return;
        }
        if (!merged.some(existing => existing.id === photo.id)) merged.push(photo);
      });
      return merged;
    });
    setPhotoNotice(notice);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const removePhoto = (id: string) => {
    setPhotos(prev => {
      const target = prev.find(photo => photo.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter(photo => photo.id !== id);
    });
    setPhotoNotice(null);
  };
  const message = buildEstimateMessage(values);
  const smsHref = smsHrefFor(message);
  const mailHref = mailHrefFor(company.email, values.name, message);
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setStatus('submitting');
    const files = photos.map(photo => photo.file);
    const canShareFiles = files.length > 0 && typeof navigator !== 'undefined' && typeof navigator.share === 'function' && typeof navigator.canShare === 'function' && navigator.canShare({
      files
    });
    if (canShareFiles) {
      try {
        await navigator.share({
          title: 'Free estimate request — A&J Pool Plaster',
          text: message,
          files
        });
        setSharedWithPhotos(true);
        setStatus('success');
        return;
      } catch (shareError) {
        if ((shareError as DOMException)?.name === 'AbortError') {
          setStatus('idle');
          return;
        }
      }
    }
    try {
      window.location.href = smsHref;
      window.setTimeout(() => setStatus('success'), 600);
    } catch {
      setStatus('error');
    }
  };
  if (status === 'success') {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.25,
      ease
    }} role="status" className="rounded-lg bg-white p-7 sm:p-8" data-id="element-56" data-mp="2hqou7g44keok">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-sky-600" data-id="element-57" data-mp="3a798x0xnvnyr">
          <CheckIcon className="h-6 w-6" data-id="element-58" data-mp="3k1x17v9lr0lz" />
        </span>
        <h3 className="mt-5 font-display text-2xl font-bold text-navy-800" data-id="element-59" data-mp="3f3vgl5wj2t8e">
          Almost done, {values.name.split(' ')[0]} — hit send.
        </h3>
        <p className="mt-3 text-[15px] leading-relaxed text-navy-500" data-id="element-60" data-mp="3b0ys4ykb42wj">
          {sharedWithPhotos ? `Your details and ${photos.length} photo${photos.length === 1 ? '' : 's'} are queued in the app you picked. Press send and we'll reply by ${values.contactMethod.toLowerCase()} within one business day to schedule your free on-site estimate in ${values.zip}.` : `Your messaging app should have opened with everything filled in for ${company.phoneDisplay}. Press send and we'll reply by ${values.contactMethod.toLowerCase()} within one business day to schedule your free on-site estimate in ${values.zip}.`}
        </p>
        {photos.length > 0 && !sharedWithPhotos && <p className="mt-3 flex items-start gap-2 rounded-md bg-sky-50 p-3.5 text-[13px] leading-relaxed text-navy-600" data-id="element-61" data-mp="2dgmat9mqyerf">
            <CameraIcon className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" data-id="element-62" data-mp="xvbnkz4e0b34" />
            Your {photos.length === 1 ? 'photo' : `${photos.length} photos`} couldn&apos;t be
            attached automatically on this device — tap the attachment button in the message and
            pick them from your gallery before sending.
          </p>}
        <dl className="mt-6 space-y-2 border-t border-navy-100 pt-5 text-sm" data-id="element-63" data-mp="a7aik06f3csg">
          <div className="flex justify-between gap-4" data-id="element-64" data-mp="f4augrpy79rh">
            <dt className="text-navy-400" data-id="element-65" data-mp="f2pv8osov2na">Service</dt>
            <dd className="text-right font-medium text-navy-700" data-id="element-66" data-mp="j5mi4rp9hkx1">{values.service}</dd>
          </div>
          <div className="flex justify-between gap-4" data-id="element-67" data-mp="ny7cbuc2y013">
            <dt className="text-navy-400" data-id="element-68" data-mp="3nkvw1ng930kk">Timeline</dt>
            <dd className="text-right font-medium text-navy-700" data-id="element-69" data-mp="3nkdymnhuqgb2">{values.timeline}</dd>
          </div>
          <div className="flex justify-between gap-4" data-id="element-70" data-mp="3rkxwxue5vpys">
            <dt className="text-navy-400" data-id="element-71" data-mp="2kyn4rxuozdqt">Phone</dt>
            <dd className="text-right font-medium text-navy-700" data-id="element-72" data-mp="2kzezl6ggt7g1">{values.phone}</dd>
          </div>
        </dl>
        <p className="mt-6 text-sm font-semibold text-navy-700" data-id="element-73" data-mp="2g26d4toaah8z">
          Didn&apos;t open? Send it another way:
        </p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row" data-id="element-74" data-mp="2pyj77snqqx49">
          <a href={smsHref} className="inline-flex items-center justify-center gap-2 rounded-md bg-navy-800 px-4 py-3 text-sm font-bold text-white transition-colors duration-150 ease-out hover:bg-navy-700" data-id="element-75" data-mp="2q0cgzgtnhxgh">
            <MessageIcon className="h-4 w-4" data-id="element-76" data-mp="3dodhq3kl89lc" />
            Open text again
          </a>
          <a href={mailHref} className="inline-flex items-center justify-center gap-2 rounded-md border border-navy-200 px-4 py-3 text-sm font-semibold text-navy-700 transition-colors duration-150 ease-out hover:border-navy-400" data-id="element-77" data-mp="38p0zjw4nvqp3">
            <MailIcon className="h-4 w-4" data-id="element-78" data-mp="3iku7f27fqw0j" />
            Send by email
          </a>
          <a href={company.phoneHref} className="inline-flex items-center justify-center gap-2 rounded-md border border-navy-200 px-4 py-3 text-sm font-semibold text-navy-700 transition-colors duration-150 ease-out hover:border-navy-400" data-id="element-79" data-mp="56cttwu6u4bn">
            <PhoneIcon className="h-4 w-4" data-id="element-80" data-mp="136rh0a74xag" />
            Call instead
          </a>
        </div>
        <button type="button" onClick={() => {
        setValues(initialValues);
        setStatus('idle');
      }} className="mt-6 block text-sm font-semibold text-navy-400 underline-offset-4 transition-colors duration-150 ease-out hover:text-navy-600 hover:underline" data-id="element-81" data-mp="3ikhouzpreeh9">
          Send another request
        </button>
      </motion.div>;
  }
  return <form onSubmit={handleSubmit} noValidate className="rounded-lg bg-white p-6 sm:p-8" aria-label="Free estimate request" data-id="element-82" data-mp="2zmwwhinscxrw">
      <h3 className="font-display text-xl font-bold text-navy-800" data-id="element-83" data-mp="17md84snzttbd">Request your free estimate</h3>
      <p className="mt-1.5 text-sm text-navy-400" data-id="element-84" data-mp="1bnysez2ux9vl">
        Takes about 40 seconds. Your answers open a ready-to-send text to{' '}
        {company.phoneDisplay} — just press send.
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-85" data-mp="xqik1i103617">
        <div className="sm:col-span-2" data-id="element-86" data-mp="12obs6glv6a44">
          <label htmlFor="name" className="block text-sm font-semibold text-navy-700" data-id="element-87" data-mp="12p3mlo3q61st">
            Full name <span className="text-ember" data-id="element-88" data-mp="1qf7cytrwk51v">*</span>
          </label>
          <input id="name" name="name" type="text" autoComplete="name" value={values.name} onChange={e => update('name', e.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'name-error' : undefined} placeholder="Marisol Reyes" className={`mt-1.5 ${inputClass} ${errors.name ? 'border-ember' : 'border-navy-200'}`} data-id="element-89" data-mp="1vamr7mtto2yt" />
          {errors.name && <p id="name-error" className="mt-1.5 flex items-center gap-1.5 text-[13px] text-ember" data-id="element-90" data-mp="2l3x1h07q4r3">
              <AlertIcon className="h-3.5 w-3.5" data-id="element-91" data-mp="3trx4h2lln042" />
              {errors.name}
            </p>}
        </div>

        <div data-id="element-92" data-mp="7ef2bdpdmdgw">
          <label htmlFor="phone" className="block text-sm font-semibold text-navy-700" data-id="element-93" data-mp="7dx1s4vold36">
            Phone <span className="text-ember" data-id="element-94" data-mp="3ft6ysykprs43">*</span>
          </label>
          <input id="phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" value={values.phone} onChange={e => update('phone', e.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'phone-error' : undefined} placeholder="(832) 555-0199" className={`mt-1.5 ${inputClass} ${errors.phone ? 'border-ember' : 'border-navy-200'}`} data-id="element-95" data-mp="3fvtejlhrddxe" />
          {errors.phone && <p id="phone-error" className="mt-1.5 flex items-center gap-1.5 text-[13px] text-ember" data-id="element-96" data-mp="3sx1hhgrp35jr">
              <AlertIcon className="h-3.5 w-3.5" data-id="element-97" data-mp="1p6upei9f1j9" />
              {errors.phone}
            </p>}
        </div>

        <div data-id="element-98" data-mp="5qs9j5ectpw3">
          <label htmlFor="email" className="block text-sm font-semibold text-navy-700" data-id="element-99" data-mp="arg05pyo84ps">
            Email
          </label>
          <input id="email" name="email" type="email" autoComplete="email" value={values.email} onChange={e => update('email', e.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} placeholder="you@email.com" className={`mt-1.5 ${inputClass} ${errors.email ? 'border-ember' : 'border-navy-200'}`} data-id="element-100" data-mp="3a7midrvkkqi0" />
          {errors.email && <p id="email-error" className="mt-1.5 flex items-center gap-1.5 text-[13px] text-ember" data-id="element-101" data-mp="2vfkkgdd287km">
              <AlertIcon className="h-3.5 w-3.5" data-id="element-102" data-mp="2nw1wrtsvfq5p" />
              {errors.email}
            </p>}
        </div>

        <div data-id="element-103" data-mp="2dzyk3u0e894i">
          <label htmlFor="zip" className="block text-sm font-semibold text-navy-700" data-id="element-104" data-mp="2dzgpt2xak4z8">
            City or ZIP <span className="text-ember" data-id="element-105" data-mp="29zem6o7zx1hb">*</span>
          </label>
          <input id="zip" name="zip" type="text" autoComplete="postal-code" value={values.zip} onChange={e => update('zip', e.target.value)} aria-invalid={Boolean(errors.zip)} aria-describedby={errors.zip ? 'zip-error' : undefined} placeholder="Katy, 77494" className={`mt-1.5 ${inputClass} ${errors.zip ? 'border-ember' : 'border-navy-200'}`} data-id="element-106" data-mp="2a0zee76u88c0" />
          {errors.zip && <p id="zip-error" className="mt-1.5 flex items-center gap-1.5 text-[13px] text-ember" data-id="element-107" data-mp="1h9z9zv4lh2jt">
              <AlertIcon className="h-3.5 w-3.5" data-id="element-108" data-mp="3hc75pjqvfx6o" />
              {errors.zip}
            </p>}
        </div>

        <div data-id="element-109" data-mp="3lebxecxrejqo">
          <label htmlFor="service" className="block text-sm font-semibold text-navy-700" data-id="element-110" data-mp="3lcr40qn6l6ue">
            What do you need?
          </label>
          <select id="service" name="service" value={values.service} onChange={e => update('service', e.target.value)} className={`mt-1.5 border-navy-200 ${inputClass}`} data-id="element-111" data-mp="37kb96d4fof4k">
            {serviceOptions.map(option => <option key={option} data-id="element-112" data-mp="32om7o48uso5m">{option}</option>)}
          </select>
        </div>

        <div className="sm:col-span-2" data-id="element-113" data-mp="2ope46z5nqtvm">
          <label htmlFor="timeline" className="block text-sm font-semibold text-navy-700" data-id="element-114" data-mp="2tmcv78u0ykpl">
            How soon?
          </label>
          <select id="timeline" name="timeline" value={values.timeline} onChange={e => update('timeline', e.target.value)} className={`mt-1.5 border-navy-200 ${inputClass}`} data-id="element-115" data-mp="mx83g469rgw7">
            {timelineOptions.map(option => <option key={option} data-id="element-116" data-mp="x16az2jtatbq">{option}</option>)}
          </select>
        </div>

        <fieldset className="sm:col-span-2" data-id="element-117" data-mp="1asjrs8qc5d32">
          <legend className="text-sm font-semibold text-navy-700" data-id="element-118" data-mp="1atcsot2m58da">Best way to reach you</legend>
          <div className="mt-2 flex flex-wrap gap-2" data-id="element-119" data-mp="15yp46u7m0dyp">
            {contactMethods.map(method => {
            const isActive = values.contactMethod === method;
            return <label key={method} className={`cursor-pointer rounded-md border px-4 py-2.5 text-sm font-semibold transition-colors duration-150 ease-out ${isActive ? 'border-sky-500 bg-sky-50 text-sky-700' : 'border-navy-200 text-navy-500 hover:border-navy-400'}`} data-id="element-120" data-mp="1fspdz9du67dr">
                  <input type="radio" name="contactMethod" value={method} checked={isActive} onChange={() => update('contactMethod', method)} className="sr-only" data-id="element-121" data-mp="3ri8ejyb0op68" />
                  {method}
                </label>;
          })}
          </div>
        </fieldset>

        <div className="sm:col-span-2" data-id="element-122" data-mp="2dedm1f68w0d1">
          <label htmlFor="details" className="block text-sm font-semibold text-navy-700" data-id="element-123" data-mp="2ddcazap8bdvb">
            Anything we should know? <span className="font-normal text-navy-300" data-id="element-124" data-mp="2ibvvr7s36jm3">(optional)</span>
          </label>
          <textarea id="details" name="details" rows={3} value={values.details} onChange={e => update('details', e.target.value)} placeholder="Approximate pool size, cracks, stains, last time it was plastered…" className={`mt-1.5 resize-y border-navy-200 ${inputClass}`} data-id="element-125" data-mp="2n5hzubk6pf6a" />
        </div>

        <div className="sm:col-span-2" data-id="element-126" data-mp="pguwvg7kbl8r">
          <p className="text-sm font-semibold text-navy-700" data-id="element-127" data-mp="phxltilbrdm1">
            Photos of your pool{' '}
            <span className="font-normal text-navy-300" data-id="element-128" data-mp="lh40mwz9ocwu">(optional, up to {MAX_PHOTOS})</span>
          </p>
          <p className="mt-1 text-[13px] leading-snug text-navy-400" data-id="element-129" data-mp="gi0vzdn788pe">
            A couple of pictures of the cracks, staining, or tile line let us quote far more
            accurately before we ever visit.
          </p>

          <input ref={fileInputRef} id="photos" name="photos" type="file" accept="image/*" multiple onChange={e => addPhotos(e.target.files)} className="sr-only" data-id="element-130" data-mp="145r3r9xvus7s" />

          <div className="mt-3 flex flex-wrap gap-3" data-id="element-131" data-mp="udlck2fggetn">
            {photos.map(photo => <div key={photo.id} className="relative h-24 w-24 overflow-hidden rounded-md border border-navy-200" data-id="element-132" data-mp="1rx6arg0ltxpd">
                <img src={photo.url} alt={`Pool photo: ${photo.file.name}`} className="h-full w-full object-cover" data-id="element-133" data-mp="vfqwr755wy1t" />
                <button type="button" onClick={() => removePhoto(photo.id)} aria-label={`Remove ${photo.file.name}`} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-navy-900/80 text-white transition-colors duration-150 ease-out hover:bg-navy-900" data-id="element-134" data-mp="mgwtixxbt9fa">
                  <PlusIcon className="h-3.5 w-3.5 rotate-45" data-id="element-135" data-mp="156vak3j3qcv2" />
                </button>
              </div>)}

            {photos.length < MAX_PHOTOS && <label htmlFor="photos" className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-navy-300 text-navy-500 transition-colors duration-150 ease-out hover:border-sky-500 hover:text-sky-600 focus-within:ring-2 focus-within:ring-sky-400" data-id="element-136" data-mp="1a7sr6bogqyug">
                <CameraIcon className="h-6 w-6" data-id="element-137" data-mp="3ic9v7r4hdz3s" />
                <span className="text-[12px] font-semibold" data-id="element-138" data-mp="3ifnyd1kyycca">
                  {photos.length === 0 ? 'Add photos' : 'Add more'}
                </span>
              </label>}
          </div>

          {photoNotice && <p className="mt-2 flex items-center gap-1.5 text-[13px] text-ember" data-id="element-139" data-mp="33nnf3n7153bi">
              <AlertIcon className="h-3.5 w-3.5" data-id="element-140" data-mp="2yplwt4hfh813" />
              {photoNotice}
            </p>}
        </div>
      </div>

      <AnimatePresence initial={false} data-id="element-141" data-mp="3df9vdxy85sl4">
        {status === 'error' && <motion.p initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} role="alert" className="mt-5 flex items-start gap-2 rounded-md border border-ember/40 bg-ember/5 p-3.5 text-[13px] leading-relaxed text-navy-700" data-id="element-142" data-mp="38mzyfxyozs9c">
            <AlertIcon className="mt-0.5 h-4 w-4 shrink-0 text-ember" data-id="element-143" data-mp="1ccbtwxinzd22" />
            We couldn&apos;t open your messaging app. Please{' '}
            <a href={mailHref} className="font-semibold text-sky-600 underline" data-id="element-144" data-mp="1m4psxiicy5kj">
              send it by email
            </a>{' '}
            or call {company.phoneDisplay} — we&apos;ll take the details over the phone.
          </motion.p>}
      </AnimatePresence>

      <button type="submit" disabled={status === 'submitting'} className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-md bg-navy-800 px-6 py-4 font-display text-base font-bold text-white transition-colors duration-150 ease-out hover:bg-navy-700 disabled:cursor-not-allowed disabled:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500" data-id="element-145" data-mp="1q6beds8yjs6c">
        {status === 'submitting' ? <>
            <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" data-id="element-146" data-mp="2hd1lwp52676q" />
            Opening your messages…
          </> : <>
            <MessageIcon className="h-5 w-5" data-id="element-147" data-mp="27n8hq978wuci" />
            {photos.length > 0 ? 'Send my request with photos' : 'Text my request to A&J'}
          </>}
      </button>

      <p className="mt-3 text-center text-[13px] text-navy-400" data-id="element-148" data-mp="1ym0mk00109mn">
        Prefer email?{' '}
        <a href={mailHref} className="font-semibold text-sky-600 underline-offset-2 hover:underline" data-id="element-149" data-mp="22ox921sospvv">
          Send the same details to {company.email}
        </a>
        . No obligation — we never share your information.
      </p>
    </form>;
}